/**
 * Keeps Konva drag/transform working when the pointer moves over another Stage.
 *
 * 1) pointer-events: none — on other stage containers so they don't steal hits.
 * 2) crossStagePointerFeedStageRef — window mousemove/touchmove/pointermove calls
 *    setPointersPositions on the source stage (see Canvas.jsx + microtask re-feed).
 *
 * We intentionally do NOT use DOM setPointerCapture on the stage container: Konva's
 * Transformer subscribes to `window` "mousemove"/"touchmove" for resize/rotate. Pointer
 * capture retargets events to the capturing element so they no longer hit `window`,
 * which stops Transformer._handleMouseMove and breaks onTransform across stages.
 */

export function beginCrossStageGesture(sourceStage, _nativeEvt, ctx) {
  endCrossStageGesture(ctx);

  if (!sourceStage) return;

  const {
    crossStagePointerFeedStageRef,
    crossStageGestureCleanupRef,
    stageRef,
    offCanvasStageRef,
    topLayerStageRef,
    offCanvasTopLayerStageRef,
  } = ctx;

  crossStagePointerFeedStageRef.current = sourceStage;

  const parts = [];

  const allStages = [
    stageRef?.current,
    offCanvasStageRef?.current,
    topLayerStageRef?.current,
    offCanvasTopLayerStageRef?.current,
  ].filter(Boolean);

  const savedStyles = [];
  for (const st of allStages) {
    if (st === sourceStage) continue;
    const el = st.container?.();
    if (!el?.style) continue;
    savedStyles.push({ el, prev: el.style.pointerEvents });
    el.style.pointerEvents = "none";
  }
  if (savedStyles.length > 0) {
    parts.push(() => {
      for (const { el, prev } of savedStyles) {
        if (el?.style) el.style.pointerEvents = prev ?? "";
      }
    });
  }

  const run = () => {
    parts.forEach((fn) => fn());
    crossStagePointerFeedStageRef.current = null;
    crossStageGestureCleanupRef.current = null;
  };
  crossStageGestureCleanupRef.current = run;
}

export function endCrossStageGesture(ctx) {
  const fn = ctx.crossStageGestureCleanupRef?.current;
  if (typeof fn === "function") {
    fn();
  }
}
